/**
 * Saves a remote file to the device.
 *
 * Replaces the previous `Browser.open(url)` approach for PDFs: a Chrome Custom Tab
 * on Android cannot render a PDF (the Android System WebView ships no PDF viewer),
 * so the user saw a blank page and no file was ever saved.
 *
 * - Web: download the bytes and hand them to the browser via an `<a download>`.
 * - Native: write the bytes into the app cache and open the system share sheet,
 *   which is where "Save to Files"/Drive/Mail/Print live on both Android and iOS.
 */

import { Capacitor } from '@capacitor/core';
import { openExternalUrl } from '@/utils/openUrl';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { loadFileBytes } from '@/utils/pdfFile';
import { logger } from '@/utils/logger';

/** Subdirectory of the app cache so our downloads are easy to identify and purge. */
const CACHE_SUBDIR = 'volyx-downloads';

/** btoa() needs a binary string; chunk it so a multi-MB file cannot blow the stack. */
function toBase64(bytes: Uint8Array): string {
  const CHUNK_SIZE = 0x8000;
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += CHUNK_SIZE) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK_SIZE));
  }
  return btoa(binary);
}

/**
 * Builds a filesystem-safe name, preferring a human title (a certificate title) and
 * falling back to the last path segment of the URL.
 */
export function deriveFileName(url: string, title?: string | null, extension = 'pdf'): string {
  const fromUrl = decodeURIComponent(url.split('?')[0].split('#')[0].split('/').pop() ?? '');
  const raw = title?.trim() || fromUrl;
  const base = raw
    // Drop an extension the title/url already carries; we re-append it below.
    .replace(new RegExp(`\\.${extension}$`, 'i'), '')
    // Allowlist letters (diacritics included), digits, underscore and dash. Dots are
    // excluded deliberately so a title like '../../etc/passwd' cannot keep its dots.
    .replace(/[^\p{L}\p{N}_-]+/gu, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80)
    .replace(/-$/, '');
  return `${base || 'document'}.${extension}`;
}

/** The share sheet rejects when the user dismisses it — that is not an error. */
function isShareCancellation(err: unknown): boolean {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return /cancel/i.test(message);
}

function saveOnWeb(bytes: Uint8Array, fileName: string, mimeType: string): void {
  const blob = new Blob([bytes as unknown as BlobPart], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick so the navigation the click started can still read it.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

async function shareOnNative(bytes: Uint8Array, fileName: string, title: string): Promise<void> {
  const { uri } = await Filesystem.writeFile({
    path: `${CACHE_SUBDIR}/${fileName}`,
    data: toBase64(bytes),
    directory: Directory.Cache,
    recursive: true,
  });

  try {
    await Share.share({ title, files: [uri], dialogTitle: title });
  } catch (err) {
    if (isShareCancellation(err)) return;
    throw err;
  }
}

export interface SaveRemoteFileOptions {
  /** Human-readable name used for the file and the share-sheet title. */
  title?: string | null;
  mimeType?: string;
}

/**
 * Saves bytes the app generated itself (an exported spreadsheet) to the device, the same
 * way as `saveRemoteFile`. Rejects if the write failed; a dismissed share sheet is not a failure.
 */
export async function saveBytes(
  bytes: Uint8Array,
  fileName: string,
  mimeType: string,
  title?: string | null,
): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      await shareOnNative(bytes, fileName, title?.trim() || fileName);
    } else {
      saveOnWeb(bytes, fileName, mimeType);
    }
  } catch (err) {
    logger.error('[saveFile] failed to save file', { fileName, err });
    throw err instanceof Error ? err : new Error(String(err));
  }
}

/**
 * Downloads `url` and saves it to the device. Resolves when the file has been saved
 * (web) or the share sheet has been dismissed (native); rejects if the download or
 * the write failed, so callers can surface a toast.
 */
export async function saveRemoteFile(url: string, options: SaveRemoteFileOptions = {}): Promise<void> {
  const { title, mimeType = 'application/pdf' } = options;
  const fileName = deriveFileName(url, title);

  let bytes: Uint8Array;
  try {
    bytes = await loadFileBytes(url);
  } catch (err) {
    logger.error('[saveFile] failed to save remote file', { url, fileName, err });
    throw err instanceof Error ? err : new Error(String(err));
  }
  await saveBytes(bytes, fileName, mimeType, title);
}

/**
 * Opens a document for viewing.
 *
 * On web the browser's own PDF viewer in a new tab is the better experience, so this
 * keeps the previous behaviour. On native there is no in-WebView viewer at all, so
 * the file is handed to the system instead — the share sheet offers both "open with"
 * and "save".
 */
export async function openRemoteFile(url: string, options: SaveRemoteFileOptions = {}): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    await openExternalUrl(url);
    return;
  }
  await saveRemoteFile(url, options);
}
